import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useOrgId } from '@/lib/pulse/useEmployee';
import { composeGreeting, type GreetingOutput } from './composeGreeting';
import { isCharlesFlagOn } from './featureFlags';

type Ctx = {
  open_action_count: number;
  top_action_title: string | null;
  prior_conversation_count: number;
  pulse_avg_30d: number | string | null;
  pulse_sample_count: number;
};

export type ReturningUserGreeting = {
  enabled: boolean;
  loading: boolean;
  isReturning: boolean;
  greeting: GreetingOutput | null;
  openActionCount: number;
  topActionTitle: string | null;
};

export function useReturningUserGreeting(): ReturningUserGreeting {
  const enabled = isCharlesFlagOn('charles_warm_greeting_v1');
  const orgId = useOrgId();
  const { user } = useAuth();
  const firstName =
    (user?.user_metadata?.first_name as string | undefined) ??
    (user?.user_metadata?.full_name as string | undefined)?.split(' ')[0] ??
    null;

  const q = useQuery({
    queryKey: ['charles', 'greeting-ctx', orgId],
    enabled: enabled && !!orgId,
    staleTime: 30_000,
    queryFn: async (): Promise<Ctx | null> => {
      const { data, error } = await supabase.rpc('my_charles_greeting_context', { _org_id: orgId! });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return (row as Ctx) ?? null;
    },
  });

  if (!enabled || !orgId) {
    return { enabled, loading: false, isReturning: false, greeting: null, openActionCount: 0, topActionTitle: null };
  }

  if (q.isLoading || !q.data) {
    return { enabled, loading: true, isReturning: false, greeting: null, openActionCount: 0, topActionTitle: null };
  }

  const ctx = q.data;
  const openActionCount = ctx.open_action_count ?? 0;
  const isReturning = openActionCount > 0 || (ctx.prior_conversation_count ?? 0) > 0;

  const pulseAvg =
    ctx.pulse_avg_30d == null
      ? null
      : typeof ctx.pulse_avg_30d === 'string'
        ? Number(ctx.pulse_avg_30d)
        : ctx.pulse_avg_30d;

  const greeting = isReturning
    ? composeGreeting({
        firstName,
        openActionCount,
        topActionTitle: ctx.top_action_title,
        pulseAvg30d: Number.isFinite(pulseAvg as number) ? (pulseAvg as number) : null,
        pulseSampleCount: ctx.pulse_sample_count ?? 0,
      })
    : null;

  return {
    enabled,
    loading: false,
    isReturning,
    greeting,
    openActionCount,
    topActionTitle: greeting?.topActionTitleDisplay ?? null,
  };
}
